// Bin settings that belong to the well filter still in flight, and whether a
// saved filter is replacing the live one. Dispatched ahead of it, every widget
// refetched the old filter with the new bins and drew bins of neither; the
// filter thunk dispatches them with the new filter id instead, in one update.
//
// Every staging bumps `revision`, and a run records it when it starts: a run
// lands only what was staged before it, so an older run finishing late never
// takes a newer saved filter's state.
//
// It sits between a filter thunk and the bin controller, so it is a store
// rather than a ref or Redux state — new filter state does not go in Redux.
import { create } from "zustand";

import { mBinSize } from "models/binSize";

export interface FilterLandingBin {
  property: string;
  bin: mBinSize;
}

export interface FilterLanding {
  bin: Nullable<FilterLandingBin>;
  // The run lands a saved filter: the checked-bins flag held back goes out with it.
  releasesHold: boolean;
}

export interface FilterLandingBinStore {
  pending: Nullable<FilterLandingBin>;
  // A saved filter is replacing the live one: the legend keeps its rows until it lands.
  isAwaitingFilter: boolean;
  revision: number;
  setPending: (pending: Nullable<FilterLandingBin>) => void;
  // Edits the queued bin for the run that already owns it: no filter change comes with
  // the edit, so no later run would carry it.
  amendPending: (bin: mBinSize) => void;
  setAwaitingFilter: (isAwaitingFilter: boolean) => void;
  // Hands a run that started at `revision` what was staged before it, and clears it.
  land: (revision: number) => Nullable<FilterLanding>;
  // A run that started at `revision` failed: its hold is released, but the bin waits
  // for the next run, since the Max Bins input already shows it.
  settle: (revision: number) => void;
  reset: () => void;
}

const cleared = { pending: null, isAwaitingFilter: false };

export const useFilterLandingBinStore = create<FilterLandingBinStore>()((set, get) => ({
  ...cleared,
  revision: 0,
  // Only a staging bumps: a clear is nothing a later run carries, and bumping for it
  // would keep the run in flight from landing (and releasing) what it owns.
  setPending: (pending) =>
    set(pending ? { pending, revision: get().revision + 1 } : { pending }),
  amendPending: (bin) => {
    const { pending } = get();
    if (pending) set({ pending: { ...pending, bin } });
  },
  setAwaitingFilter: (isAwaitingFilter) =>
    set(
      isAwaitingFilter
        ? { isAwaitingFilter, revision: get().revision + 1 }
        : { isAwaitingFilter }
    ),
  land: (revision) => {
    const { pending, isAwaitingFilter } = get();
    if (get().revision > revision) return null;
    set(cleared);
    return { bin: pending, releasesHold: isAwaitingFilter };
  },
  settle: (revision) => {
    if (get().revision <= revision) set({ isAwaitingFilter: false });
  },
  // The revision only ever grows: a run from before the reset must not take what is
  // staged after it.
  reset: () => set(cleared)
}));

export const selectIsAwaitingFilter = (s: FilterLandingBinStore): boolean =>
  s.isAwaitingFilter;

/** The MaxBins `property` will have once the well filter in flight lands. */
export function landingMaxBins(
  property: string,
  current: Maybe<mBinSize["MaxBins"]>
): Maybe<mBinSize["MaxBins"]> {
  const { pending } = useFilterLandingBinStore.getState();
  return pending?.property === property ? pending.bin.MaxBins : current;
}
